import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Scale, TrendingUp, TrendingDown, Info, AlertTriangle, Plus, X, ArrowRight } from 'lucide-react'
import {
  getDirectorAccounts, getOwnerSegments, getSettlements, createSettlement,
} from '../../services/api'

const fmt = (n) =>
  new Intl.NumberFormat('en-UG', { maximumFractionDigits: 0 }).format(Math.round(n || 0))

// Minimal set of debtor -> creditor payments that brings everyone to zero.
// Greedy: repeatedly match the biggest debtor to the biggest creditor.
function suggestSettlements(rows) {
  const creditors = rows.filter(o => o.net > 0.5)
    .map(o => ({ id: o.owner_segment_id, name: o.name, amt: o.net })).sort((a, b) => b.amt - a.amt)
  const debtors = rows.filter(o => o.net < -0.5)
    .map(o => ({ id: o.owner_segment_id, name: o.name, amt: -o.net })).sort((a, b) => b.amt - a.amt)
  const out = []
  let i = 0, j = 0
  while (i < debtors.length && j < creditors.length) {
    const pay = Math.min(debtors[i].amt, creditors[j].amt)
    if (pay > 0.5) {
      out.push({ fromId: debtors[i].id, from: debtors[i].name,
                 toId: creditors[j].id, to: creditors[j].name, amount: Math.round(pay) })
    }
    debtors[i].amt -= pay
    creditors[j].amt -= pay
    if (debtors[i].amt <= 0.5) i++
    if (creditors[j].amt <= 0.5) j++
  }
  return out
}

function SettlementForm({ ranchId, owners, prefill, onClose }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    from_owner_segment_id: prefill?.fromId || '',
    to_owner_segment_id: prefill?.toId || '',
    amount: prefill?.amount ? String(prefill.amount) : '',
    settled_on: new Date().toISOString().split('T')[0],
    notes: '',
  })
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: createSettlement,
    onSuccess: () => {
      queryClient.invalidateQueries(['directorAccounts', ranchId])
      queryClient.invalidateQueries(['settlements', ranchId])
      onClose()
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to record settlement'),
  })

  const submit = (e) => {
    e.preventDefault()
    setError('')
    if (form.from_owner_segment_id === form.to_owner_segment_id) {
      setError('From and to owners must differ'); return
    }
    mutation.mutate({
      ranch_id: ranchId,
      from_owner_segment_id: form.from_owner_segment_id,
      to_owner_segment_id: form.to_owner_segment_id,
      amount: parseFloat(form.amount),
      settled_on: form.settled_on,
      notes: form.notes || null,
    })
  }

  const sel = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm'
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-medium text-gray-900">Record a settlement</h3>
        <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Records that one owner <em>paid</em> another (e.g. a debtor paying back whoever covered a cost).
      </p>
      {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">From (who paid)</label>
          <select className={sel} required value={form.from_owner_segment_id}
            onChange={(e) => setForm({ ...form, from_owner_segment_id: e.target.value })}>
            <option value="">— select —</option>
            {owners?.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To (who received)</label>
          <select className={sel} required value={form.to_owner_segment_id}
            onChange={(e) => setForm({ ...form, to_owner_segment_id: e.target.value })}>
            <option value="">— select —</option>
            {owners?.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Amount (UGX)</label>
          <input className={sel} type="number" min="1" required value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date</label>
          <input className={sel} type="date" required value={form.settled_on}
            onChange={(e) => setForm({ ...form, settled_on: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <input className={sel} placeholder="Notes (optional)" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={mutation.isPending} className="btn btn-primary">Save settlement</button>
        </div>
      </form>
    </div>
  )
}

export default function DirectorAccounts({ ranchId }) {
  // null = form closed; an object = open (with optional prefill from a suggestion)
  const [settle, setSettle] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['directorAccounts', ranchId],
    queryFn: () => getDirectorAccounts(ranchId),
    enabled: !!ranchId,
  })
  const { data: owners } = useQuery({
    queryKey: ['ownerSegments', ranchId],
    queryFn: () => getOwnerSegments(ranchId),
    enabled: !!ranchId,
  })
  const { data: settlements } = useQuery({
    queryKey: ['settlements', ranchId],
    queryFn: () => getSettlements(ranchId),
    enabled: !!ranchId,
  })

  const rows = data?.owners || []
  const owed = rows.filter(o => o.net > 0.005)
  const owes = rows.filter(o => o.net < -0.005)
  const flags = data?.mismatch_flags || []
  const suggestions = suggestSettlements(rows)

  return (
    <div className="max-w-5xl mx-auto p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Scale className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Director Accounts</h1>
          {data?.finance_mode && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
              {data.finance_mode}
            </span>
          )}
        </div>
        <button onClick={() => setSettle({})} className="btn btn-secondary">
          <Plus className="h-4 w-4 mr-1" /> Record settlement
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Each owner&apos;s position on shared costs and sale proceeds.{' '}
        <span className="text-green-700">Positive = the group owes them</span>;{' '}
        <span className="text-red-700">negative = they owe the group</span>.
      </p>

      {settle !== null && (
        <SettlementForm ranchId={ranchId} owners={owners} prefill={settle.prefill}
          onClose={() => setSettle(null)} />
      )}

      {/* Operating-float positions (mig 118): deposits vs allocated draw-downs.
          Separate from the who-owes-who nets — float expenses net to zero
          there by construction; the funding question lives here. */}
      {data?.float?.uses_float && (
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <h2 className="font-semibold text-gray-900 mb-1">Operating float</h2>
          <p className="text-xs text-gray-500 mb-3">
            What each owner has prepaid into the farm float, minus their share of
            float-paid expenses (quarterly herd-share ratio).
            <span className="text-green-700"> Positive = prepaid credit</span>;
            <span className="text-red-700"> negative = behind on contributions</span>.
          </p>
          {data.float.awaiting_payment?.count > 0 && (
            <p className="text-xs text-amber-700 mb-2">
              {data.float.awaiting_payment.count} approved float expense(s) totalling{' '}
              {fmt(data.float.awaiting_payment.total)} are awaiting payment recording —
              balances draw down only when payment is recorded.
            </p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {data.float.balances.map((b) => (
              <div key={b.owner_segment_id}
                className={`p-2 rounded-lg border text-sm ${b.in_arrears
                  ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'}`}>
                <p className="text-gray-600">{b.name}</p>
                <p className={`font-semibold ${b.balance < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                  {fmt(b.balance)}
                </p>
                <p className="text-xs text-gray-400">
                  in {fmt(b.deposits)}
                  {b.sale_credits > 0 ? <> · sales {fmt(b.sale_credits)}</> : null}
                  {' '}· used {fmt(b.drawdown)}
                </p>
                {b.is_institutional && b.balance < 0 && (
                  <p className="text-xs text-gray-500 italic">
                    claim against future sale proceeds
                  </p>
                )}
                {b.pending_drawdown > 0 && (
                  <p className="text-xs text-amber-700">
                    committed {fmt(b.pending_drawdown)} → {fmt(b.projected_balance)} after payment
                  </p>
                )}
                {b.in_arrears && (
                  <p className="text-xs text-red-600">behind on contributions</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-16 text-center text-gray-400">Loading…</div>
      ) : (
        <>
          {flags.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-6">
              <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
                <AlertTriangle className="h-4 w-4" /> Sales to reconcile ({flags.length})
              </div>
              <p className="text-xs text-amber-700 mb-2">
                Proceeds went to someone other than the animal&apos;s owner (a common-herd or
                another owner&apos;s animal).
              </p>
              {flags.map(f => (
                <div key={f.income_id} className="text-sm py-0.5 flex justify-between">
                  <span>{f.income_date}: {f.description}</span>
                  <span className="text-amber-800">
                    {fmt(f.amount)} — {f.owner_name}&apos;s animal, received by {f.received_by}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* To settle up — the actionable list: who should pay whom to zero everyone out */}
          {suggestions.length > 0 && (
            <div className="rounded-xl border border-primary-200 bg-primary-50/40 p-4 mb-6">
              <div className="flex items-center gap-2 text-primary-800 font-medium mb-1">
                <ArrowRight className="h-4 w-4" /> To settle up
              </div>
              <p className="text-xs text-gray-600 mb-3">
                The fewest payments that bring everyone to zero. Click one to record it once the money changes hands.
              </p>
              <div className="space-y-1">
                {suggestions.map((s, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white rounded-lg border border-gray-100 px-3 py-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium">{s.from}</span>
                      <ArrowRight className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">{s.to}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold">{fmt(s.amount)}</span>
                      <button
                        onClick={() => setSettle({ prefill: s })}
                        className="text-xs text-primary-700 hover:underline">
                        Record
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="rounded-xl border border-green-200 bg-green-50/50 p-4">
              <div className="flex items-center gap-2 text-green-800 font-medium mb-2">
                <TrendingUp className="h-4 w-4" /> Owed by the group
              </div>
              {owed.length ? owed.map(o => (
                <div key={o.owner_segment_id} className="flex justify-between text-sm py-0.5">
                  <span>{o.name}{o.is_company ? ' (company)' : ''}</span>
                  <span className="font-semibold text-green-700">+{fmt(o.net)}</span>
                </div>
              )) : <div className="text-sm text-gray-400">None</div>}
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50/50 p-4">
              <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
                <TrendingDown className="h-4 w-4" /> Owes the group
              </div>
              {owes.length ? owes.map(o => (
                <div key={o.owner_segment_id} className="flex justify-between text-sm py-0.5">
                  <span>{o.name}{o.is_company ? ' (company)' : ''}</span>
                  <span className="font-semibold text-red-700">{fmt(o.net)}</span>
                </div>
              )) : <div className="text-sm text-gray-400">None</div>}
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Owner</th>
                  <th className="text-right px-4 py-2 font-medium">Paid</th>
                  <th className="text-right px-4 py-2 font-medium">Owes (share)</th>
                  <th className="text-right px-4 py-2 font-medium">Sales</th>
                  <th className="text-right px-4 py-2 font-medium">Settlements</th>
                  <th className="text-right px-4 py-2 font-medium">Net (UGX)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(o => (
                  <tr key={o.owner_segment_id} className="border-t border-gray-100">
                    <td className="px-4 py-2">
                      {o.name}
                      {o.is_company && <span className="ml-1 text-xs text-gray-400">(company)</span>}
                    </td>
                    <td className="text-right px-4 py-2 text-gray-700">{fmt(o.contributed)}</td>
                    <td className="text-right px-4 py-2 text-gray-700">{fmt(o.charged)}</td>
                    <td className="text-right px-4 py-2 text-gray-500 text-xs">
                      {o.sale_credit > 0 ? <div className="text-green-600">+{fmt(o.sale_credit)} owed</div> : null}
                      {o.sale_received > 0 ? <div>−{fmt(o.sale_received)} banked</div> : null}
                      {o.sale_credit === 0 && o.sale_received === 0 ? '—' : null}
                    </td>
                    <td className="text-right px-4 py-2 text-gray-500 text-xs">
                      {o.settled_paid > 0 ? <div>paid {fmt(o.settled_paid)}</div> : null}
                      {o.settled_received > 0 ? <div>received {fmt(o.settled_received)}</div> : null}
                      {o.settled_paid === 0 && o.settled_received === 0 ? '—' : null}
                    </td>
                    <td className={`text-right px-4 py-2 font-semibold ${
                      o.net > 0.005 ? 'text-green-700' : o.net < -0.005 ? 'text-red-700' : 'text-gray-500'
                    }`}>
                      {o.net > 0 ? '+' : ''}{fmt(o.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {settlements?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Settlement history</h3>
              <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                {settlements.map(s => (
                  <div key={s.id} className="px-4 py-2 text-sm flex justify-between">
                    <span>{s.settled_on}: {s.from_name} paid {s.to_name}{s.notes ? ` (${s.notes})` : ''}</span>
                    <span className="font-medium">{fmt(s.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex items-start gap-2 text-xs text-gray-500">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Net = Paid − Owes(share) + Sales + Settlements. Shares use live verified head counts,
              snapshotted per expense. Own-animal sales cancel out; the flagged sales above are the
              ones to reconcile. &quot;To settle up&quot; is the shortest path to clearing every balance.
            </span>
          </div>
        </>
      )}
    </div>
  )
}
